import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { countryCodeFor } from '@repo/front-end-utils';
import type { LatLng } from '@repo/front-end-utils';
import type { AdminTwin } from '@repo/frontend-types';

import { addressQuery, useAddressGeocode, type UseAddressGeocode } from './useAddressGeocode';
import {
  formFromTwin,
  isLocated,
  looksLikePlaceholderOrigin,
  positionPatch,
  type TwinDetailsForm,
  type TwinPosition,
} from './twinDetailsForm';
import type { TwinDetailsSave } from './useTwinDetailsSave';
import type { WashedFlyRequest } from '../../components/3D/planView/planPlacementRenderer';

/**
 * Saving an address flies the washed map there, and sets the twin's position while it has none
 * (`onboarding-persistent-scene-twin-details-map`, R3).
 *
 * **At page level, not in Twin details** (plan ruling 1): the step unmounts once its fade ends after
 * *Next*, so a lookup it owned would be dropped before the save it waits on lands, and the position
 * would never be set. The page mounts this once and drives it from the save queue's `onSaved`.
 *
 * - **When (R3.1).** After an accepted save of an address line or the country (the page bumps
 *   `addressSavedTick`), once the queue has nothing held, in flight or failed. A failed save holds
 *   the queue, so nothing runs until *Retry saving* succeeds. And once when Twin details opens for a
 *   twin with no real position whose address is already saved, since saving it unchanged sends
 *   nothing (review ruling, Maria, 2026-11-02).
 * - **Not at all (R3.2)** without a postcode, or for the same address and country as the last
 *   lookup this visit. A twin switch forgets the last address. The postcode rule (review ruling,
 *   Maria, 2026-11-02) keeps a first line alone from setting a rough position that a fuller address
 *   could then no longer correct.
 * - **On a match (R3.3).** While the twin has no real position (none, or the legacy placeholder), the
 *   match is saved as its position, exactly as *Find* saved it (`positionPatch(…, 'address')`), and
 *   the map flies there. Once it has one, the map only flies.
 * - **No match, or a failed lookup (R3.4):** no flight, no save; `lookup.state` holds *Find*'s own
 *   sentence for the position card to show.
 * - **Overtaken (Failure States).** Each lookup takes a sequence number; only the latest, for the
 *   twin still on screen, with the page still mounted, saves or flies. A result that lands while
 *   another save is waiting is held until the queue is quiet, then dropped if an address save was
 *   accepted meanwhile (that newer address has its own lookup), else used (review ruling, Maria,
 *   2026-11-03): an older address's match never becomes the position over a newer address.
 * - **A refused route** (no `?twinId=`, or two identifiers naming different twins) looks nothing up,
 *   and a result still in flight when the route is refused is dropped: the wizard is not shown, so
 *   nothing may be written behind its refusal screen (Copilot review on #2505).
 * - **The create route (R3.5)** needs nothing here: the create form has no address, and this runs
 *   only from saves the queue accepted, which need a twin.
 *
 * Bounds: the geocode is `useAddressGeocode`'s (8 s per attempt, no retry, `null` on failure); the
 * position write is the queue's (20 s, *Retry saving*).
 */

interface Options {
  /** The twin on screen. */
  twinBid: string | undefined;
  /** The page's settled record for it, updated by each accepted save (`recordAfterSave`). */
  twin: AdminTwin | null;
  /** Advanced by the page each time the queue accepts a save carrying an address line or country. */
  addressSavedTick: number;
  save: Pick<TwinDetailsSave, 'pending' | 'save' | 'unsavedPosition'>;
  /** The organisation's Mapbox token claim, as Twin details passed it to the lookup. */
  mapboxToken: string | undefined;
  /** Twin details is the step on screen: an unplaced twin's saved address is looked up once. */
  onTwinDetails?: boolean;
  /** False while the page refuses the route: nothing is looked up, saved or flown. */
  enabled?: boolean;
}

export interface AddressFlight {
  /** The lookup, for the position card's outcome line. */
  lookup: UseAddressGeocode;
  /** A one-shot request for the stage's washed map to fly to a point; a new `id` is a new request. */
  flyRequest: WashedFlyRequest | null;
}

/** What a lookup searches with: the address line and the country that biases it (as *Find*). */
const searchKey = (f: TwinDetailsForm) => `${addressQuery(f)}|${countryCodeFor(f.country) ?? ''}`;

const text = (value: unknown): string => (value == null ? '' : String(value));

/** The twin's position now: a position still being saved wins over the record's. */
const positionNow = (
  twin: AdminTwin | null,
  unsaved: TwinDetailsSave['unsavedPosition'],
): TwinPosition =>
  unsaved
    ? { latitude: text(unsaved.latitude), longitude: text(unsaved.longitude) }
    : { latitude: text(twin?.latitude), longitude: text(twin?.longitude) };

/** Whether the twin has a real position now: located, and not the legacy placeholder. */
const placedNow = (
  twin: AdminTwin | null,
  unsaved: TwinDetailsSave['unsavedPosition'],
): boolean => {
  const now = positionNow(twin, unsaved);
  return isLocated(now) && !looksLikePlaceholderOrigin(now);
};

export const useAddressFlight = ({
  twinBid,
  twin,
  addressSavedTick,
  save,
  mapboxToken,
  onTwinDetails = false,
  enabled = true,
}: Options): AddressFlight => {
  const form = useMemo(() => formFromTwin(twin), [twin]);
  const lookup = useAddressGeocode(form, mapboxToken);
  const [flyRequest, setFlyRequest] = useState<WashedFlyRequest | null>(null);

  // Read when an answer lands: each may have changed while looking up.
  const twinRef = useRef(twin);
  twinRef.current = twin;
  const saveRef = useRef(save);
  saveRef.current = save;
  const twinBidRef = useRef(twinBid);
  twinBidRef.current = twinBid;
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const seqRef = useRef(0);
  const flyIdRef = useRef(0);
  const lastKeyRef = useRef<string | null>(null);
  /** The address-save tick the latest lookup began at, while it is still out; else null. */
  const outstandingTickRef = useRef<number | null>(null);
  // Refused: any lookup still out is dropped, and none starts until the route is accepted.
  const enabledRef = useRef(enabled);
  if (enabledRef.current !== enabled) {
    enabledRef.current = enabled;
    if (!enabled) seqRef.current += 1;
  }
  const handledTickRef = useRef(addressSavedTick);
  // A twin switch: forget the last address, take the tick as handled, and drop any lookup in flight.
  const forTwinRef = useRef(twinBid);
  if (forTwinRef.current !== twinBid) {
    forTwinRef.current = twinBid;
    lastKeyRef.current = null;
    handledTickRef.current = addressSavedTick;
    seqRef.current += 1;
    outstandingTickRef.current = null;
  }

  // The lookup lives at page level, so a twin switch would otherwise carry one twin's outcome onto
  // the next twin's card, and let a lookup still in flight land there. `reset` clears both.
  const reset = lookup.reset;
  const resetForTwinRef = useRef(twinBid);
  useEffect(() => {
    if (resetForTwinRef.current === twinBid) return;
    resetForTwinRef.current = twinBid;
    reset();
    // A request the last twin's map had not yet taken is not this twin's to fly.
    setFlyRequest(null);
  }, [twinBid, reset]);

  const search = lookup.search;
  // The address saves accepted so far, read when a result is used.
  const tickRef = useRef(addressSavedTick);
  tickRef.current = addressSavedTick;
  /** A result that landed while another save was waiting, used once the queue is quiet. */
  const heldRef = useRef<{
    seq: number;
    tick: number;
    forTwin: string | undefined;
    match: LatLng;
  } | null>(null);

  /** Set below: `use` re-runs a lookup it had to drop, and is declared before `lookUp`. */
  const lookUpRef = useRef<(current: TwinDetailsForm, forTwin: string | undefined) => void>(
    () => undefined,
  );

  /** Use a lookup's match: save it as the position while unplaced, and fly (R3.3). */
  const use = useCallback(
    (seq: number, tick: number, forTwin: string | undefined, match: LatLng) => {
      // A refusal bumps `seqRef`, so a lookup out when the route was refused is dropped here too.
      if (!mountedRef.current || seq !== seqRef.current || twinBidRef.current !== forTwin) return;
      // A newer address save was accepted after this lookup began: its own lookup decides. That
      // newer save may have put back the address this lookup searched (A, then B, then A again
      // while the queue was busy), which the newer lookup skipped as already searched; forget the
      // key so the address on record now is looked up (Copilot review on #2505).
      if (tickRef.current !== tick) {
        lastKeyRef.current = null;
        if (!saveRef.current.pending && handledTickRef.current === tickRef.current) {
          lookUpRef.current(formFromTwin(twinRef.current), forTwin);
        }
        return;
      }
      if (saveRef.current.pending) {
        heldRef.current = { seq, tick, forTwin, match };
        return;
      }
      if (!placedNow(twinRef.current, saveRef.current.unsavedPosition)) {
        const patch = positionPatch(
          { latitude: String(match.lat), longitude: String(match.lng) },
          'address',
        );
        if (patch) saveRef.current.save(patch, forTwin);
      }
      flyIdRef.current += 1;
      setFlyRequest({ id: flyIdRef.current, longitude: match.lng, latitude: match.lat });
    },
    [],
  );

  /** Look `current` up for `forTwin`, then use the match (R3.2 to R3.4). */
  const lookUp = useCallback(
    (current: TwinDetailsForm, forTwin: string | undefined) => {
      if (!enabledRef.current || current.postcode.trim() === '') return;
      const key = searchKey(current);
      // The same address as the last lookup is not searched again, unless that lookup is still out
      // and an address save has been accepted since: its answer will be dropped, so search again.
      const outstanding = outstandingTickRef.current;
      const overtaken = outstanding !== null && outstanding !== tickRef.current;
      if (key === lastKeyRef.current && !overtaken) return;
      lastKeyRef.current = key;
      const seq = ++seqRef.current;
      const tick = tickRef.current;
      outstandingTickRef.current = tick;
      search(current)
        .then(match => {
          if (seq === seqRef.current) outstandingTickRef.current = null;
          if (match) use(seq, tick, forTwin, match);
        })
        .catch((error: unknown) => {
          if (seq === seqRef.current) outstandingTickRef.current = null;
          // `geocodeAddress` answers `null` rather than throwing, so this is a bug if it runs. No
          // address: the twin's BID and the error only.
          // eslint-disable-next-line no-console
          console.warn('[useAddressFlight] lookup failed', { twinBid: forTwin, error });
        });
    },
    [search, use],
  );
  lookUpRef.current = lookUp;

  // A held result, once the queue is quiet: `use` drops it if an address save came in meanwhile.
  useEffect(() => {
    if (save.pending || !heldRef.current) return;
    const held = heldRef.current;
    heldRef.current = null;
    use(held.seq, held.tick, held.forTwin, held.match);
  }, [save.pending, addressSavedTick, use]);

  // After an accepted address save, once the queue is quiet (R3.1).
  useEffect(() => {
    if (addressSavedTick === handledTickRef.current || save.pending) return;
    handledTickRef.current = addressSavedTick;
    lookUp(formFromTwin(twinRef.current), twinBid);
  }, [addressSavedTick, save.pending, twinBid, lookUp]);

  // On opening Twin details for a twin with no real position and a saved address: once per twin
  // per visit.
  const arrivedRef = useRef(new Set<string>());
  useEffect(() => {
    if (!enabled || !onTwinDetails || !twinBid || !twin || twin.bid !== twinBid || save.pending)
      return;
    if (arrivedRef.current.has(twinBid)) return;
    arrivedRef.current.add(twinBid);
    if (placedNow(twin, save.unsavedPosition)) return;
    lookUp(formFromTwin(twin), twinBid);
  }, [enabled, onTwinDetails, twinBid, twin, save.pending, save.unsavedPosition, lookUp]);

  return { lookup, flyRequest };
};
